import { createFileRoute, redirect } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { supabase } from "@/lib/supabase";
import { apiClient } from "@/lib/api";

export const Route = createFileRoute("/auth/callback")({
  component: AuthCallback,
});

function AuthCallback() {
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent multiple executions
    if (hasProcessed.current) {
      return;
    }

    const handleAuthCallback = async () => {
      try {
        hasProcessed.current = true;
        
        console.log("🔄 Starting auth callback process...");
        
        await supabase.auth.exchangeCodeForSession(window.location.href);
        // Handle the OAuth callback with URL parameters
        const { data, error } = await supabase.auth.getSession();

        if (error) {
          console.error("Auth error:", error);
          window.location.href = "/login?error=auth_failed";
          return;
        }

        if (data.session) {
          console.log("✅ Session found, calling server callback...");
          
          // Call your server-side auth callback to set up the user properly
          try {
            const response = await fetch(
              `${import.meta.env.VITE_API_URL}/api/auth/callback`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${data.session.access_token}`,
                },
                body: JSON.stringify({
                  access_token: data.session.access_token,
                  refresh_token: data.session.refresh_token,
                  user: data.session.user,
                }),
              }
            );

            if (!response.ok) {
              const errorText = await response.text();
              console.error("❌ Server response error:", response.status, errorText);
              throw new Error(`Server auth callback failed: ${response.status}`);
            }

            const result = await response.json();
            console.log("✅ Server auth callback successful:", result);
          } catch (serverError) {
            console.error("❌ Server auth callback error:", serverError);
            // Continue with login even if server callback fails
          }

          // Successful login - redirect to dashboard
          console.log("🎉 Redirecting to dashboard...");
          window.location.href = "/dashboard";
        } else {
          console.error("❌ No session found");
          // No session found - redirect back to login
          window.location.href = "/login?error=no_session";
        }
      } catch (err) {
        console.error("❌ Callback error:", err);
        hasProcessed.current = false; // Reset on error to allow retry
        window.location.href = "/login?error=callback_failed";
      }
    };

    handleAuthCallback();
  }, []); // Empty dependency array to run only once

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-yellow-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Completing sign in...</p>
        <p className="text-sm text-gray-500 mt-2">Setting up your account...</p>
      </div>
    </div>
  );
}
